// NPM packages
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Alert, Typography } from '@mui/material';
import styled from '@emotion/styled';

// All other imports
import PageContent from 'components/core/PageContent';
import TextLink from 'components/core/TextLink';
import { useServices } from 'services';
import H1 from 'components/core/H1';

const Root = styled(PageContent)({});

const StepTitle = styled(Typography)({
  marginTop: '2rem',
  marginBottom: '0.5rem',
});

const StepDescription = styled(Typography)({});

export interface GetStartedProps {
  className?: string;
}

export default function GetStarted(
  props: GetStartedProps
): React.ReactElement | null {
  const { className } = props;
  const { authService, envService } = useServices();
  const location = useLocation();

  return (
    <Root className={className}>
      <H1>Getting Started Guide</H1>
      <Typography>
        Welcome to MSD-LIVE, a one-stop shop for finding software, data,
        workflows, and tools used by the{' '}
        <TextLink to="https://multisectordynamics.org/">
          MultiSector Dynamics Community
        </TextLink>
        . This page describes the basic steps needed to get started working with
        MSD-LIVE resources. For more information about MSD-LIVE and our team,
        please see the{' '}
        <TextLink to="/about" aria-label="about">
          About
        </TextLink>{' '}
        page.
      </Typography>

      <Alert severity="info">
        Many improvements and new features are planned for throughout the next
        year, so please check back periodically to learn of all the latest
        changes!
      </Alert>

      <StepTitle variant="h3">Step 1: Create An Account</StepTitle>
      <StepDescription>
        MSD-LIVE user accounts are managed by the Amazon Cognito Identity
        Management System so that access can be shared across all MSD-LIVE
        products and services. Please go to the{' '}
        <TextLink to="/sign-up" aria-label="sign-up">
          Sign Up
        </TextLink>{' '}
        page to create a new account. If you can't remember your password or
        aren't sure whether you have an account or not, please go to the{' '}
        <TextLink to="/forgot-password" aria-label="forgot-password">
          Forgot Password
        </TextLink>{' '}
        page and enter your email. If you have an account, you will receive a
        link to reset your password, if you don't have an account, it will let
        you know and direct you to the Sign Up page.
      </StepDescription>

      <StepTitle variant="h3">Step 2: Log In</StepTitle>
      <StepDescription>
        If you are part of the MSD community, please make sure to{' '}
        <TextLink to={authService.getLogInUrl({ location })}>Log In</TextLink>{' '}
        before interacting with the MSD-LIVE web sites to ensure you have access
        to all the available functionality. A log in is not required to browse
        the data repository or download data from MSD-LIVE.
      </StepDescription>

      <StepTitle variant="h3">Step 3: Join A Project</StepTitle>
      <StepDescription>
        In order to upload datasets or set up compute resources, you MUST belong
        to at least one MSD-LIVE project. MSD-LIVE projects directly map to MSD
        projects such as IM3 and PCHES. Please browse the{' '}
        <TextLink to="/projects" aria-label="projects">
          Projects
        </TextLink>{' '}
        page to see the current list of projects. If you see a "Join" button
        next to the project, you can press it to request membership. You will be
        notified when your request is approved. If you would like a new project
        created, please email the MSD-LIVE team at{' '}
        <TextLink to="mailto:info@msdlive.org">info@msdlive.org</TextLink>.
      </StepDescription>

      <StepTitle variant="h3">Step 4: Publish Code or Data</StepTitle>
      <StepDescription>
        If you have project datasets or code that you would like to share with
        the MSD-LIVE community or the general public, you can upload them to our{' '}
        <TextLink
          to={`${envService.getValue('DATA_REPO_URL')}/uploads/new`}
          aria-label="Data Repository"
        >
          Data Repository
        </TextLink>
        . Our upload form provides a rich set of metadata to help others find
        your data. It also gives you the opportunity to mint a DOI for your data
        if desired. In order to upload data, you must belong to at least one
        project and the dataset must be directly funded by and relevant to that
        project. When uploading your data, you must select one or more projects
        to associate your submission with, and one project is also assigned the
        special role of "Reviewing Project". When you are ready to publish, a
        curator from the Reviewing Project will review your submission to ensure
        it follows recommended best practices and and meets all{' '}
        <TextLink to="/policies">MSD-LIVE Policies</TextLink>. Submissions can
        be marked as public or restricted. Public submissions are visible to the
        general public, whereas restricted submissions are only viewable by the
        projects they belong to.
      </StepDescription>
      <StepDescription>
        The MSD-LIVE Data Repository is based upon the{' '}
        <TextLink to="https://inveniosoftware.org/products/rdm/">
          Invenio Research Data Management (RDM) platform
        </TextLink>
        , which is in turn based upon a refactoring of the popular{' '}
        <TextLink to="https://zenodo.org/">
          Zenodo research data repository
        </TextLink>
        . Many new RDM features are planned over the next year and, where
        appropriate, we will incorporate them into the MSD-LIVE Data Repository
        as soon as they are released.
      </StepDescription>

      <StepTitle variant="h3">Step 5: Find Data</StepTitle>
      <StepDescription>
        Please go the the Data Repository's{' '}
        <TextLink
          to={`${envService.getValue('DATA_REPO_URL')}/search`}
          aria-label="Data Repository"
        >
          Search
        </TextLink>{' '}
        page to find data or code to use for your research. The search page
        allows you to search across all the datasets and code stored in MSD-LIVE
        using the same metadata properties captured in the upload form. Note
        that you will only be able to see datasets for which you have the
        correct permissions. Over the next year we are working to add the
        ability to connect data hosted in the Data Repository to cloud computing
        resources in MSD-LIVE.
      </StepDescription>

      <StepTitle variant="h3">Step 6: Set Up Compute Resources</StepTitle>
      <StepDescription>
        As the core MSD-LIVE infrastructure is built on top of the AWS cloud, we
        can provide the ability to quickly provision scalable compute resources
        for your project's needs. This can include things like Jupyter
        Hub/Jupyter Notebooks for demos, workshops, or research, web application
        frameworks, and scalable compute clusters for running models or
        analytics. Because cloud resources are provisioned via reusable
        templates, this reduces the time to set up such resources to a matter of
        minutes. Over time, MSD-LIVE will be adding more and more templates
        tailored to the MSD community. Please see the{' '}
        <TextLink
          to="/computational-resources"
          aria-label="computational-resources"
        >
          Computational Resources
        </TextLink>{' '}
        page for more information including a list of our current offerings.
      </StepDescription>

      <StepTitle variant="h3">Step 7: Get Help</StepTitle>
      <StepDescription>
        Please see our{' '}
        <TextLink to="/resources" aria-label="resources">
          Resources
        </TextLink>{' '}
        page for informational videos explaining various MSD-LIVE components
        and/or community best practices. If you run into problems or need
        additional assistance with any of the above steps, please see our{' '}
        <TextLink to="/help" aria-label="help">
          Help
        </TextLink>{' '}
        page to look for known issues or contact the MSD-LIVE team.
      </StepDescription>
    </Root>
  );
}
